import { useEffect } from "react";
import {
  MenuButton,
  Modal,
  Card,
  TitleLayout,
} from "@velocity-ehs/component-library";
import { useHistory } from "react-router";
import { OrgUnit } from "./Hierarchies";
import LinkedLocationsTable from "./LinkedLocationsTable";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";
import classes from "./OrgUnitProfile.module.scss";

interface Props {
  orgUnit?: OrgUnit;
  onAddOrgUnit: () => void;
  onMoveOrgUnit: () => void;
  onDeleteOrgUnit: () => void;
  isLoading: boolean;
  isDeleting: boolean;
  closeDeleteModal: () => void;
  openDeleteModal: () => void;
  isDeleteModalOpen: boolean;
}

const OrgUnitDetails: React.FC<Props> = (props) => {
  const cx = classNames.bind(classes);
  const history = useHistory();
  const { t } = useTranslation();
  const { reset } = useForm<{ lineage: string }>({ mode: "onBlur" });

  const actionButtons = (): any[] => {
    const orgUnit = props.orgUnit;
    if (!orgUnit) return [];
    const items = [
      {
        label: t("action_add_org_unit"),
        command: props.onAddOrgUnit,
        show: orgUnit.viewerCanAddChildOrgUnit,
      },
      {
        label: t("action_manage"),
        command: () => history.push(`/orgUnit/${orgUnit.id}/edit`),
        show: orgUnit.viewerCanManageOrgUnit,
      },
      {
        label: t("action_move"),
        command: props.onMoveOrgUnit,
        show: orgUnit.viewerCanMoveOrgUnit,
      },
      {
        label: t("universal_Delete"),
        command: props.openDeleteModal,
        show: orgUnit.viewerCanDeleteOrgUnit,
      },
    ];

    return items.filter((item) => item.show);
  };

  const headerAction = () => {
    if (!props.orgUnit && actionButtons().length === 0) return <></>;
    return (
      <MenuButton
        label={t("universal_Actions")}
        items={actionButtons()}
        icon="pi-chevron-down"
        buttonClass="p-button-text"
      />
    );
  };

  useEffect(() => {
    reset(props.orgUnit);
  }, [props.orgUnit, reset]);

  const panel = (
    <Card
      title={
        props.isLoading
          ? undefined
          : props.orgUnit?.name ?? "No Hierarchy Selected"
      }
      headerAction={!props.isLoading ? headerAction() : undefined}
    >
      <TitleLayout text={t("hierarchies_Org_Unit_Info")} size="small" />
      <label className={cx(classes.hierarchiesDetailsLineageLabel)}>
        {t("hierarchies_Lineage")}
      </label>
      <p className={cx(classes.hierarchiesDetailsLineageValue)}>
        {props.orgUnit?.lineage || "—"}
      </p>
      <TitleLayout text={t("universal_Locations")} size="small" />
      <LinkedLocationsTable
        orgUnit={props.orgUnit}
        isLoading={props.isLoading}
        isEditMode={false}
      />
    </Card>
  );

  return (
    <>
      {panel}
      <Modal
        title={t("action_delete_org_unit")}
        show={props.isDeleteModalOpen}
        onClose={props.closeDeleteModal}
        footerButtons={[
          {
            label: t("universal_Delete"),
            onClick: props.onDeleteOrgUnit,
            loading: props.isDeleting,
            id: "deleteOrgUnitModal",
          },
          {
            label: t("universal_Cancel"),
            className: "p-button-text",
            onClick: props.closeDeleteModal,
            id: "cancelDeleteOrgUnitModal",
          },
        ]}
      >
        {t("hierarchies_OrgUnitDetails_deleting_organization_message")}
      </Modal>
    </>
  );
};

export default OrgUnitDetails;
